import React, { useState, useMemo, useEffect } from "react";
import { useParams } from "react-router";
import { useLocation, useNavigate } from "react-router-dom";

import {
  FiMapPin,
  FiCalendar,
  FiMusic,
  FiUsers,
  FiShoppingBag,
  FiPlay,
  FiDisc,
  FiArrowLeft,
  FiExternalLink,
  FiShare2,
  FiMic,
  FiAward,
  FiInfo,
  FiImage,
  FiX,
} from "react-icons/fi";
import {
  FaSpotify,
  FaYoutube,
  FaInstagram,
  FaFacebookF,
  FaApple,
  FaSoundcloud,
  FaTwitter,
  FaGlobeAmericas,
} from "react-icons/fa";
import MainContainer from "../../../components/utils/MainContainer";
import Particles from "../../../components/utils/Particles";
import {
  Container,
  HeroSection,
  HeroContent,
  BandLogo,
  BandTitle,
  BandSubtitle,
  QuickActions,
  ActionButton,
  ContentGrid,
  MainContent,
  Sidebar,
  Section,
  SectionTitle,
  SectionContent,
  BiographyText,
  InfoGrid,
  InfoCard,
  InfoLabel,
  InfoValue,
  MemberCard,
  MemberPhoto,
  MemberInfo,
  MemberName,
  MemberRole,
  MemberBio,
  DiscographyGrid,
  AlbumCard,
  AlbumCover,
  AlbumInfo,
  AlbumTitle,
  AlbumYear,
  AlbumTracks,
  TrackList,
  TrackItem,
  TrackNumber,
  TrackName,
  TrackDuration,
  EventCard,
  EventDate,
  EventInfo,
  EventVenue,
  EventCity,
  EventTickets,
  GalleryGrid,
  GalleryItem,
  StatsCard,
  StatNumber,
  StatLabel,
  SocialLinksExtended,
  SocialLink,
  GenreTag,
  AchievementsList,
  AchievementItem,
  TabNavigation,
  TabButton,
  TabContent,
  EmptyState,
  EmptyStateIcon,
  EmptyStateText,
  BackLink,
  ModalOverlay,
  ModalContent,
  ModalImage,
  CloseButton,
} from "./BandDetailsPage.styles";
import { GiTicket } from "react-icons/gi";
import { useDispatch, useSelector } from "react-redux";
import { selectPublicBandDetails } from "../../../store/slices/bands/slice";
import {
  getBandByName,
  getBandPublicDetails,
} from "../../../store/slices/bands/asyncThunks";
import { trackBandView } from "../../../store/slices/viewTracking/asyncThunks";
import Loader from "../../../components/utils/Loader";
import { toast } from "../../../components/modern-ui/sonner";

export default function BandDetailsPage() {
  const [activeTab, setActiveTab] = useState("biography");
  const [selectedImage, setSelectedImage] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    data: bandData,
    loading,
    error,
  } = useSelector(selectPublicBandDetails);

  const { bandId } = useParams();
  const location = useLocation();
  const bandName = location.state?.bandName;

  useEffect(() => {
    if (bandId) {
      dispatch(getBandPublicDetails(bandId));
      dispatch(trackBandView(bandId));
    } else if (bandName) {
      dispatch(getBandByName(bandName));
    }
  }, [dispatch, bandId, bandName]);

  // Handle escape key for modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && isModalOpen) {
        closeImageModal();
      }
    };

    if (isModalOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden"; // Prevent background scrolling
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [isModalOpen]);

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: `${bandData.name}`,
        text: `Pogledajte informacije o bendu ${bandData.name}`,
        url: window.location.href,
      });
    } else {
      // Fallback - kopiraj URL
      navigator.clipboard.writeText(window.location.href);

      toast.success("Link je kopiran u clipboard");
    }
  };

  const openImageModal = (image) => {
    setSelectedImage(image);
    setIsModalOpen(true);
  };

  const closeImageModal = () => {
    setIsModalOpen(false);
    setSelectedImage(null);
  };

  // Get banner image for HeroSection background
  const bannerImage = useMemo(() => {
    if (bandData?.gallery) {
      const banner = bandData.gallery.find((img) => img.isBanner);
      return banner?.url;
    }
    return null;
  }, [bandData?.gallery]);

  const tabs = [
    { id: "biography", label: "Biografija", icon: FiMusic },
    { id: "members", label: "Članovi", icon: FiUsers },
    { id: "discography", label: "Diskografija", icon: FiDisc },
    { id: "events", label: "Koncerti", icon: FiCalendar },
    { id: "gallery", label: "Galerija", icon: FiExternalLink },
  ];

  if (loading) {
    return (
      <MainContainer>
        <Particles />
        <Container>
          <BackLink
            onClick={() => {
              navigate(-1);
              window.scrollTo(0, 0);
            }}
          >
            <FiArrowLeft />
            Nazad
          </BackLink>
          <Loader title="detalja benda" />
        </Container>
      </MainContainer>
    );
  }

  if (error) {
    return (
      <MainContainer>
        <Container>
          <BackLink
            onClick={() => {
              navigate(-1);
              window.scrollTo(0, 0);
            }}
          >
            <FiArrowLeft />
            Nazad
          </BackLink>
          <div
            style={{ color: "#e74c3c", textAlign: "center", padding: "2rem" }}
          >
            <h2>Greška pri učitavanju</h2>
            <p>{error}</p>
          </div>
        </Container>
      </MainContainer>
    );
  }

  if (!bandData) {
    return (
      <MainContainer>
        <Container>
          <BackLink
            onClick={() => {
              navigate(-1);
              window.scrollTo(0, 0);
            }}
          >
            <FiArrowLeft />
            Nazad
          </BackLink>
          <h2>Bend nije pronađen</h2>
        </Container>
      </MainContainer>
    );
  }

  return (
    <>
      <MainContainer>
        <Particles />
        <Container>
          <BackLink
            onClick={() => {
              navigate(-1);
              window.scrollTo(0, 0);
            }}
          >
            <FiArrowLeft />
            Nazad
          </BackLink>

          <HeroSection $backgroundImage={bannerImage || bandData.bannerImage}>
            <HeroContent>
              <BandLogo>{bandData.name}</BandLogo>
              <BandTitle>{bandData.name}</BandTitle>
              <BandSubtitle>
                <GenreTag $genre={bandData.genre}>
                  {bandData.genre.toUpperCase()}
                </GenreTag>
                <span>
                  {bandData.city}
                  {bandData.country && `, ${bandData.country}`}
                </span>
                <span>Osnovan {bandData.founded}</span>
              </BandSubtitle>

              <QuickActions>
                {bandData?.socialLinks?.spotify && (
                  <ActionButton
                    $primary
                    onClick={() => {
                      window.open(bandData.socialLinks?.spotify, "_blank");
                    }}
                  >
                    <FiPlay />
                    Slušaj na Spotify
                  </ActionButton>
                )}
                <ActionButton
                  onClick={() => {
                    navigate(`/prodavnica?bend=${bandData.id}`);
                    setTimeout(() => {
                      window.scrollTo(0, 0);
                    }, 300);
                  }}
                >
                  <FiShoppingBag />
                  Merchandise
                </ActionButton>

                <ActionButton onClick={handleShare}>
                  <FiShare2 />
                  Podeli
                </ActionButton>
              </QuickActions>
            </HeroContent>
          </HeroSection>

          <TabNavigation>
            {tabs?.map((tab) => {
              const Icon = tab.icon;
              return (
                <TabButton
                  key={tab.id}
                  $active={activeTab === tab.id}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <Icon />
                  {tab.label}
                </TabButton>
              );
            })}
          </TabNavigation>

          <ContentGrid>
            <MainContent>
              <TabContent>
                {activeTab === "biography" && (
                  <Section>
                    <SectionTitle>
                      <FiMusic />O bendu
                    </SectionTitle>
                    <SectionContent>
                      {bandData.biography ? (
                        <BiographyText>
                          {bandData.biography
                            .split("\n")
                            ?.map((paragraph, index) => (
                              <p key={index}>{paragraph.trim()}</p>
                            ))}
                        </BiographyText>
                      ) : (
                        <EmptyState>
                          <EmptyStateIcon>
                            <FiInfo />
                          </EmptyStateIcon>
                          <EmptyStateText>
                            Biografija benda nije dostupna
                          </EmptyStateText>
                        </EmptyState>
                      )}
                      {bandData.achievements &&
                        bandData.achievements.length > 0 && (
                          <AchievementsList>
                            <h4>Dostignuća</h4>
                            {bandData.achievements?.map(
                              (achievement, index) => (
                                <AchievementItem key={index}>
                                  <FiAward size={16} />

                                  <div>
                                    <div>
                                      {typeof achievement === "string"
                                        ? achievement
                                        : achievement.title ||
                                          achievement.name ||
                                          "Dostignuće"}
                                    </div>
                                    {achievement.year && (
                                      <div
                                        style={{
                                          fontSize: "0.8rem",
                                          opacity: 0.7,
                                          marginTop: "4px",
                                        }}
                                      >
                                        {achievement.year}
                                      </div>
                                    )}
                                  </div>
                                </AchievementItem>
                              )
                            )}
                          </AchievementsList>
                        )}

                      {/* <EmptyState style={{ marginTop: "2rem" }}>
                        <EmptyStateIcon>
                          <FiAward />
                        </EmptyStateIcon>

                        <EmptyStateText>
                          Nema zabeleženih dostignuća
                        </EmptyStateText>
                      </EmptyState> */}
                    </SectionContent>
                  </Section>
                )}

                {activeTab === "members" && (
                  <Section>
                    <SectionTitle>
                      <FiUsers />
                      Članovi benda
                    </SectionTitle>
                    <SectionContent>
                      {bandData.members && bandData.members.length > 0 ? (
                        bandData.members?.map((member, index) => (
                          <MemberCard key={index + "@"}>
                            <MemberPhoto>
                              <FiMic size={40} />
                            </MemberPhoto>
                            <MemberInfo>
                              <MemberName>
                                {typeof member === "string"
                                  ? member
                                  : member.name || "Nepoznat član"}
                              </MemberName>
                              {member.role && (
                                <MemberRole>{member.role}</MemberRole>
                              )}
                              {member.bio && (
                                <MemberBio>{member.bio}</MemberBio>
                              )}
                              {member.joined && (
                                <div
                                  style={{
                                    color: "#ff6b35",
                                    fontSize: "0.9rem",
                                  }}
                                >
                                  U bendu od {member.joined}. godine
                                </div>
                              )}
                            </MemberInfo>
                          </MemberCard>
                        ))
                      ) : (
                        <EmptyState>
                          <EmptyStateIcon>
                            <FiUsers />
                          </EmptyStateIcon>
                          <EmptyStateText>
                            Informacije o članovima nisu dostupne
                          </EmptyStateText>
                        </EmptyState>
                      )}
                    </SectionContent>
                  </Section>
                )}

                {activeTab === "discography" && (
                  <Section>
                    <SectionTitle>
                      <FiDisc />
                      Diskografija
                    </SectionTitle>
                    <SectionContent>
                      {bandData.discography &&
                      bandData.discography.length > 0 &&
                      bandData.discography.some(album => album.title && album.title.trim()) ? (
                        <DiscographyGrid>
                          {bandData.discography?.filter(album => album.title && album.title.trim()).map((album, index) => (
                            <AlbumCard key={index + "@"}>
                              <AlbumCover>
                                <FiDisc size={60} />
                              </AlbumCover>
                              <AlbumInfo>
                                <AlbumTitle>
                                  {album.name || album.title || "Bez naziva"}
                                </AlbumTitle>
                                <AlbumYear>
                                  {album.year || "N/A"} •{" "}
                                  {album.type || "Album"}
                                </AlbumYear>
                                {album.tracks && album.tracks.length > 0 && (
                                  <AlbumTracks>
                                    <TrackList>
                                      {album.tracks?.map((track, i) => (
                                        <TrackItem key={i}>
                                          <TrackNumber>{i + 1}</TrackNumber>
                                          <TrackName>{track.name}</TrackName>
                                          <TrackDuration>
                                            {track.duration}
                                          </TrackDuration>
                                        </TrackItem>
                                      ))}
                                    </TrackList>
                                  </AlbumTracks>
                                )}
                              </AlbumInfo>
                            </AlbumCard>
                          ))}
                        </DiscographyGrid>
                      ) : (
                        <EmptyState>
                          <EmptyStateIcon>
                            <FiDisc />
                          </EmptyStateIcon>
                          <EmptyStateText>
                            Diskografija benda nije dostupna
                          </EmptyStateText>
                        </EmptyState>
                      )}
                    </SectionContent>
                  </Section>
                )}

                {activeTab === "events" && (
                  <Section>
                    <SectionTitle>
                      <FiCalendar />
                      Predstojeći koncerti
                    </SectionTitle>
                    <SectionContent>
                      {bandData.events && bandData.events.length > 0 ? (
                        bandData.events?.map((event, index) => (
                          <EventCard key={index}>
                            <EventDate>
                              <div className="month">
                                {new Date(event.date).toLocaleDateString(
                                  "sr-Latn-RS",
                                  {
                                    month: "short",
                                  }
                                )}
                              </div>
                              <div className="day">
                                {new Date(event.date).getDate()}
                              </div>
                            </EventDate>
                            <EventInfo>
                              <EventVenue>{event.venue}</EventVenue>
                              <EventCity>
                                <FiMapPin />
                                {event.city}, {event.country}
                              </EventCity>
                            </EventInfo>
                            <EventTickets>
                              {event.ticketUrl && (
                                <ActionButton
                                  onClick={() =>
                                    window.open(event.ticketUrl, "_blank")
                                  }
                                  $primary={event.status === "available"}
                                  disabled={event.status === "sold-out"}
                                >
                                  <GiTicket />
                                  {event.status === "available"
                                    ? "Kupi karte"
                                    : "Rasprodato"}
                                </ActionButton>
                              )}
                            </EventTickets>
                          </EventCard>
                        ))
                      ) : (
                        <EmptyState>
                          <EmptyStateIcon>
                            <FiCalendar />
                          </EmptyStateIcon>
                          <EmptyStateText>
                            Nema zakazanih koncerata
                          </EmptyStateText>
                        </EmptyState>
                      )}
                    </SectionContent>
                  </Section>
                )}

                {activeTab === "gallery" && (
                  <Section>
                    <SectionTitle>
                      <FiImage />
                      Foto galerija
                    </SectionTitle>
                    <SectionContent>
                      {bandData.gallery && bandData.gallery.length > 0 ? (
                        <GalleryGrid>
                          {[...bandData.gallery]
                            ?.sort((a, b) => {
                              // Sort by isMain first (main image first), then by sortOrder
                              if (a.isMain && !b.isMain) return -1;
                              if (!a.isMain && b.isMain) return 1;
                              return (a.sortOrder || 0) - (b.sortOrder || 0);
                            })
                            ?.map((photo, index) => (
                              <GalleryItem
                                key={photo.id || index}
                                onClick={() => openImageModal(photo)}
                              >
                                {photo.url ? (
                                  <img
                                    src={photo.url}
                                    alt={
                                      photo.originalName ||
                                      `Slika benda ${bandData.name} ${
                                        index + 1
                                      }`
                                    }
                                    onError={(e) => {
                                      // Fallback to placeholder if image fails to load
                                      e.target.style.display = "none";
                                      e.target.nextElementSibling.style.display =
                                        "flex";
                                    }}
                                  />
                                ) : null}

                                {/* Fallback placeholder */}
                                <div
                                  className="placeholder"
                                  style={{
                                    display: photo.url ? "none" : "flex",
                                  }}
                                >
                                  <FiImage size={30} />
                                  <span>Slika nije dostupna</span>
                                </div>

                                {/* Image info overlay */}
                                {/* {photo.originalName && (
                                  <div className="image-info">
                                    {photo.originalName}
                                  </div>
                                )} */}
                              </GalleryItem>
                            ))}
                        </GalleryGrid>
                      ) : (
                        <EmptyState>
                          <EmptyStateIcon>
                            <FiImage />
                          </EmptyStateIcon>
                          <EmptyStateText>
                            Foto galerija nije dostupna
                          </EmptyStateText>
                        </EmptyState>
                      )}
                    </SectionContent>
                  </Section>
                )}
              </TabContent>
            </MainContent>

            <Sidebar>
              <Section>
                <SectionTitle>Statistike</SectionTitle>
                <SectionContent>
                  {bandData.stats ? (
                    <>
                      <StatsCard>
                        <StatNumber>{bandData.stats?.years || 0}</StatNumber>
                        <StatLabel>Godina rada</StatLabel>
                      </StatsCard>
                      <StatsCard>
                        <StatNumber>{bandData.stats?.albums || 0}</StatNumber>
                        <StatLabel>Albuma</StatLabel>
                      </StatsCard>

                      <StatsCard>
                        <StatNumber>
                          {bandData.stats?.numOfProducts || 0}
                        </StatNumber>
                        <StatLabel>Proizvoda</StatLabel>
                      </StatsCard>
                      <StatsCard>
                        <StatNumber>
                          {bandData.stats?.numOfNews || 0}
                        </StatNumber>
                        <StatLabel>Vesti</StatLabel>
                      </StatsCard>
                    </>
                  ) : (
                    <EmptyState>
                      <EmptyStateIcon>
                        <FiInfo />
                      </EmptyStateIcon>
                      <EmptyStateText>Statistike nisu dostupne</EmptyStateText>
                    </EmptyState>
                  )}
                </SectionContent>
              </Section>

              <Section>
                <SectionTitle>Informacije</SectionTitle>
                <SectionContent>
                  <InfoGrid>
                    <InfoCard>
                      <InfoLabel>Status</InfoLabel>
                      <InfoValue>{bandData.status || "Nepoznato"}</InfoValue>
                    </InfoCard>
                    <InfoCard>
                      <InfoLabel>Osnovan</InfoLabel>
                      <InfoValue>{bandData.founded || "Nepoznato"}</InfoValue>
                    </InfoCard>
                    <InfoCard>
                      <InfoLabel>Lokacija</InfoLabel>
                      <InfoValue>
                        {bandData.city
                          ? `${bandData.city}${
                              bandData.country ? `, ${bandData.country}` : ""
                            }`
                          : "Nepoznato"}
                      </InfoValue>
                    </InfoCard>
                  </InfoGrid>
                </SectionContent>
              </Section>

              {bandData.socialLinks?.spotify ||
              bandData.socialLinks?.youtube ||
              bandData.socialLinks?.instagram ||
              bandData.socialLinks?.facebook ||
              bandData.socialLinks?.apple ||
              bandData.socialLinks?.soundcloud ? (
                <Section>
                  <SectionTitle>Društvene mreže</SectionTitle>
                  <SectionContent>
                    <SocialLinksExtended>
                      {bandData.socialLinks.spotify && (
                        <SocialLink
                          href={bandData.socialLinks.spotify}
                          target="_blank"
                        >
                          <FaSpotify />
                          <span>Spotify</span>
                        </SocialLink>
                      )}
                      {bandData.socialLinks.youtube && (
                        <SocialLink
                          href={bandData.socialLinks.youtube}
                          target="_blank"
                        >
                          <FaYoutube />
                          <span>YouTube</span>
                        </SocialLink>
                      )}
                      {bandData.socialLinks.instagram && (
                        <SocialLink
                          href={bandData.socialLinks.instagram}
                          target="_blank"
                        >
                          <FaInstagram />
                          <span>Instagram</span>
                        </SocialLink>
                      )}
                      {bandData.socialLinks.facebook && (
                        <SocialLink
                          href={bandData.socialLinks.facebook}
                          target="_blank"
                        >
                          <FaFacebookF />
                          <span>Facebook</span>
                        </SocialLink>
                      )}

                      {bandData.socialLinks.twitter && (
                        <SocialLink
                          href={bandData.socialLinks.twitter}
                          target="_blank"
                        >
                          <FaTwitter />
                          <span>Twitter</span>
                        </SocialLink>
                      )}
                      {bandData.socialLinks.apple && (
                        <SocialLink
                          href={bandData.socialLinks.apple}
                          target="_blank"
                        >
                          <FaApple />
                          <span>Apple Music</span>
                        </SocialLink>
                      )}
                      {bandData.socialLinks.soundcloud && (
                        <SocialLink
                          href={bandData.socialLinks.soundcloud}
                          target="_blank"
                        >
                          <FaSoundcloud />
                          <span>SoundCloud</span>
                        </SocialLink>
                      )}
                      {bandData.socialLinks.website && (
                        <SocialLink
                          href={bandData.socialLinks.website}
                          target="_blank"
                        >
                          <FaGlobeAmericas />
                          <span>Website</span>
                        </SocialLink>
                      )}
                    </SocialLinksExtended>
                  </SectionContent>
                </Section>
              ) : (
                <Section>
                  <SectionTitle>Društvene mreže</SectionTitle>
                  <SectionContent>
                    <EmptyState>
                      <EmptyStateIcon>
                        <FiShare2 />
                      </EmptyStateIcon>
                      <EmptyStateText>
                        Društvene mreže nisu dostupne
                      </EmptyStateText>
                    </EmptyState>
                  </SectionContent>
                </Section>
              )}
            </Sidebar>
          </ContentGrid>
        </Container>
      </MainContainer>

      {/* Image Modal */}
      {isModalOpen && selectedImage && (
        <ModalOverlay onClick={closeImageModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <CloseButton onClick={closeImageModal}>
              <FiX />
            </CloseButton>
            <ModalImage
              src={selectedImage.url}
              alt={selectedImage.originalName || `Slika benda ${bandData.name}`}
              onError={(e) => {
                e.target.src =
                  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300'%3E%3Crect width='100%25' height='100%25' fill='%23333'/%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' dy='.3em' fill='%23fff'%3ESlike nije dostupna%3C/text%3E%3C/svg%3E";
              }}
            />
          </ModalContent>
        </ModalOverlay>
      )}
    </>
  );
}
